import { useQuery } from 'convex/react';
import { useParams } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type { PerSeasonRow } from '../../convex/history/franchises';
import { type Column, DataTable } from '../components/DataTable';
import { Empty } from '../components/Empty';
import { FranchiseLink } from '../components/FranchiseLink';
import { Loading } from '../components/Loading';
import { PageShell } from '../components/PageShell';
import { PlayerLink } from '../components/PlayerLink';

const FINISH_LABEL: Record<NonNullable<PerSeasonRow['finish']>, string> = {
  champion: '🏆 Champion',
  'runner-up': 'Runner-up',
  'third-place': 'Third place',
  'ultimate-loser': 'Ultimate loser',
};

const GROUP_LABEL: Record<string, string> = {
  'rookie-draft': 'Rookie draft',
  'rfa-auction': 'RFA auction',
  waiver: 'Waivers',
  trade: 'Trades',
  startup: 'Startup draft',
};

export default function HistoryFranchise(): JSX.Element {
  const { franchiseId } = useParams<{ franchiseId: string }>();
  const idArg =
    franchiseId && franchiseId.length > 0
      ? { franchiseId: franchiseId as Id<'franchises'> }
      : ('skip' as const);

  const career = useQuery(api.history.franchises.getCareer, idArg);
  const lineage = useQuery(api.history.franchises.getDynastyLineage, idArg);
  const picks = useQuery(api.history.franchises.getDraftPicksMade, idArg);
  const owners = useQuery(api.history.franchises.getOwnerHistory, idArg);

  if (!franchiseId) {
    return (
      <PageShell title="Franchise">
        <Empty>Invalid franchise id.</Empty>
      </PageShell>
    );
  }
  if (
    career === undefined ||
    lineage === undefined ||
    picks === undefined ||
    owners === undefined
  ) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }
  if (career === null) {
    return (
      <PageShell title="Franchise">
        <Empty>No franchise found for that id.</Empty>
      </PageShell>
    );
  }

  // ---- columns ----

  const perSeasonCols: Column<(typeof career.perSeason)[number]>[] = [
    {
      header: 'Year',
      cell: (r) => (
        <a href={`/history/${r.year}`} className="hover:underline">
          {r.year}
        </a>
      ),
    },
    { header: 'W', cell: (r) => r.wins, align: 'right' },
    { header: 'L', cell: (r) => r.losses, align: 'right' },
    { header: 'T', cell: (r) => r.ties, align: 'right' },
    { header: 'PF', cell: (r) => r.pointsFor.toFixed(2), align: 'right' },
    { header: 'PA', cell: (r) => r.pointsAgainst.toFixed(2), align: 'right' },
    { header: 'Finish', cell: (r) => (r.finish ? FINISH_LABEL[r.finish] : '—') },
  ];

  const picksCols: Column<(typeof picks)[number]>[] = [
    { header: 'Year', cell: (p) => p.year, align: 'right' },
    { header: 'Rd', cell: (p) => p.round, align: 'right' },
    { header: 'Slot', cell: (p) => p.slot, align: 'right' },
    { header: 'Ovr', cell: (p) => p.overall, align: 'right' },
    {
      header: 'Selected',
      cell: (p) =>
        p.selectedPlayerId && p.selectedPlayerName ? (
          <PlayerLink
            playerId={p.selectedPlayerId}
            name={p.selectedPlayerName}
            position={p.selectedPlayerPosition}
            nflTeam={p.selectedPlayerNflTeam}
          />
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      header: 'Outcome',
      cell: (p) =>
        p.traded ? (
          <span className="text-xs text-gray-600">
            traded to{' '}
            <FranchiseLink franchiseId={p.eventualFranchiseId} name={p.eventualFranchiseName} />
          </span>
        ) : (
          <span className="text-xs text-gray-500">kept</span>
        ),
    },
  ];

  const trophyLine = (label: string, years: number[]) =>
    years.length === 0 ? null : (
      <div className="text-sm">
        <span className="font-semibold mr-2">{label}:</span>
        {years
          .slice()
          .sort((a, b) => b - a)
          .map((y, i) => (
            <span key={y}>
              {i > 0 ? ', ' : ''}
              <a href={`/history/${y}`} className="hover:underline">
                {y}
              </a>
            </span>
          ))}
      </div>
    );

  const reg = career.allTime.regularSeason;
  const po = career.allTime.playoff;

  return (
    <PageShell title={career.franchise.name} subtitle={`${career.franchise.division} division`}>
      {/* All-time record */}
      <section className="mb-6 border rounded p-3 bg-white">
        <h2 className="font-semibold mb-2">All-time record</h2>
        <div className="text-sm">
          <div>
            Regular season:{' '}
            <span className="font-mono">
              {reg.wins}-{reg.losses}-{reg.ties}
            </span>
            <span className="text-gray-500 ml-2">
              PF {reg.pointsFor.toFixed(2)} · PA {reg.pointsAgainst.toFixed(2)}
            </span>
          </div>
          <div>
            Playoff:{' '}
            <span className="font-mono">
              {po.wins}-{po.losses}-{po.ties}
            </span>
            <span className="text-gray-500 ml-2">
              PF {po.pointsFor.toFixed(2)} · PA {po.pointsAgainst.toFixed(2)}
            </span>
          </div>
        </div>
      </section>

      {/* Trophy case */}
      <section className="mb-6">
        <h2 className="font-semibold mb-2">Trophy case</h2>
        {career.trophyCase.championYears.length === 0 &&
        career.trophyCase.runnerUpYears.length === 0 &&
        career.trophyCase.thirdPlaceYears.length === 0 &&
        career.trophyCase.ultimateLoserYears.length === 0 ? (
          <Empty>No postseason finishes recorded.</Empty>
        ) : (
          <div className="border rounded p-3 bg-white space-y-1">
            {trophyLine('🏆 Champion', career.trophyCase.championYears)}
            {trophyLine('Runner-up', career.trophyCase.runnerUpYears)}
            {trophyLine('Third place', career.trophyCase.thirdPlaceYears)}
            {trophyLine('Ultimate loser', career.trophyCase.ultimateLoserYears)}
          </div>
        )}
      </section>

      {/* Per-season summary */}
      <section className="mb-6">
        <h2 className="font-semibold mb-2">Season-by-season</h2>
        <DataTable
          columns={perSeasonCols}
          rows={career.perSeason}
          rowKey={(r) => r.year}
          empty="No regular-season games recorded."
        />
      </section>

      {/* Owner history */}
      <section className="mb-6">
        <h2 className="font-semibold mb-2">Owner history</h2>
        {owners.length === 0 ? (
          <Empty>
            Owner history not yet recorded. Commissioner UI for owners is planned for a future
            phase.
          </Empty>
        ) : (
          <ul className="text-sm list-disc list-inside">
            {owners.map((o) => (
              <li key={`${o.ownerName}-${o.startYear}`}>
                <span className="font-semibold">{o.ownerName}</span>{' '}
                <span className="text-gray-600">
                  ({o.startYear}–{o.endYear ?? 'present'})
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Dynasty lineage */}
      <section className="mb-6">
        <h2 className="font-semibold mb-2">Dynasty lineage</h2>
        {lineage.groups.length === 0 ? (
          <Empty>No rostered players recorded.</Empty>
        ) : (
          lineage.groups.map((g) => (
            <div key={g.group} className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">
                {GROUP_LABEL[g.group] ?? g.group}{' '}
                <span className="text-gray-400 font-normal">({g.players.length})</span>
              </h3>
              <ul className="text-sm">
                {g.players.map((p) => (
                  <li key={p.playerId} className="border-b py-1">
                    <PlayerLink
                      playerId={p.playerId}
                      name={p.playerName}
                      position={p.position}
                      nflTeam={p.nflTeam}
                    />
                    <span className="text-xs text-gray-500 ml-2">
                      acquired {p.acquisitionYear}
                      {p.acquisitionKind === 'trade' &&
                        p.originalFranchiseId !== career.franchise._id && (
                          <>
                            {' '}
                            from{' '}
                            <FranchiseLink
                              franchiseId={p.originalFranchiseId}
                              name={p.originalFranchiseName}
                            />
                          </>
                        )}
                      {' · '}
                      {p.yearsHere.length === 1
                        ? `${p.yearsHere[0]}`
                        : `${p.yearsHere[0]}–${p.yearsHere[p.yearsHere.length - 1]}`}
                      {p.departed && p.finalFranchiseId && p.finalFranchiseName && (
                        <>
                          {' · '}departed to{' '}
                          <FranchiseLink
                            franchiseId={p.finalFranchiseId}
                            name={p.finalFranchiseName}
                          />
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>

      {/* Draft picks made */}
      <section className="mb-6">
        <h2 className="font-semibold mb-2">Draft picks originally owned</h2>
        <DataTable
          columns={picksCols}
          rows={picks}
          rowKey={(p) => p.pickId}
          empty="No draft picks recorded for this franchise."
        />
      </section>
    </PageShell>
  );
}
